'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { DATA_DELETION_MESSAGES } from '@shared/constants';
import { Logger } from '@shared/logger';
import { FetchApiClient } from '@shared/httpService';
import { tryCatchAsync } from '@shared/utils';

interface DeleteDataConfirmPageProps {
  token: string | null;
}

type ConfirmState = 'loading' | 'ready' | 'confirming' | 'success' | 'error';

/**
 * @group Public
 * @summary Data deletion confirmation page component
 * 
 * <blockquote>
 * Renders the deletion confirmation page. Validates the token from the URL,
 * shows a final confirmation prompt, and executes the deletion when confirmed.
 * Handles invalid/expired tokens gracefully.
 * </blockquote>
 */
export function DeleteDataConfirmPage({ token }: DeleteDataConfirmPageProps) {
  const [state, setState] = useState<ConfirmState>('loading');
  const [errorMessage, setErrorMessage] = useState<string>('');

  // Validate token presence on mount
  useEffect(() => {
    if (!token) {
      setState('error');
      setErrorMessage(DATA_DELETION_MESSAGES.ERRORS.INVALID_TOKEN);
    } else {
      setState('ready');
    }
  }, [token]);

  const handleConfirm = async () => {
    if (!token) return;

    setState('confirming');
    setErrorMessage('');

    const wrappedResult = await tryCatchAsync(async () => {
      const fetchApi = new FetchApiClient();
      const result = await fetchApi.post('/api/data/delete-request/confirm', {
        token,
      });

      if (result.isErr) {
        const errorData = result.getFail();
        Logger.error('Deletion confirmation failed', {
          resource: 'data.deletion',
          errorCode: errorData.errorCode,
        }, new Error(errorData.errorMessage));
        return { success: false, message: DATA_DELETION_MESSAGES.ERRORS.REQUEST_FAILED };
      }

      const response = result.getSuccess();
      const data = response.data as { success?: boolean; message?: string };

      if (response.ok && data.success) {
        return { success: true };
      }
      
      return { success: false, message: data.message || DATA_DELETION_MESSAGES.ERRORS.INVALID_TOKEN };
    });

    wrappedResult.match({
      ok: (data) => {
        if (data.success) {
          setState('success');
        } else {
          setState('error');
          setErrorMessage(data.message ?? DATA_DELETION_MESSAGES.ERRORS.REQUEST_FAILED);
        }
      },
      err: (error) => {
        Logger.error('Deletion confirmation failed', {
          resource: 'data.deletion',
        }, new Error(error.errorMessage));
        setState('error');
        setErrorMessage(DATA_DELETION_MESSAGES.ERRORS.REQUEST_FAILED);
      }
    });
  };

  // Loading state
  if (state === 'loading') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  // Success state
  if (state === 'success') {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8 text-center">
          <div className="mb-6">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          </div>
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">
            Deletion Complete
          </h2>
          <p className="text-gray-600 mb-6">
            {DATA_DELETION_MESSAGES.SUCCESS.DELETION_COMPLETE}
          </p>
          <p className="text-sm text-gray-500 mb-6">
            Your account and all associated data have been permanently removed from our systems.
          </p>
          <Link 
            href="/"
            className="text-blue-600 hover:text-blue-800 underline"
          >
            Return to Home
          </Link>
        </div>
      </div>
    );
  }

  // Error state
  if (state === 'error') {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8 text-center">
          <div className="mb-6">
            <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
          </div>
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">
            Unable to Process Request
          </h2>
          <p className="text-gray-600 mb-6">
            {errorMessage}
          </p>
          <div className="space-y-3">
            <Link 
              href="/delete-data"
              className="block w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors text-center"
            >
              Request New Deletion Link
            </Link>
            <Link 
              href="/"
              className="block text-gray-500 hover:text-gray-700 underline"
            >
              Return to Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Ready/Confirming state - show confirmation prompt
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8">
        <h1 className="text-2xl font-semibold text-gray-900 mb-2 text-center">
          {DATA_DELETION_MESSAGES.UI.CONFIRM_PAGE_TITLE}
        </h1>
        <p className="text-gray-600 text-center mb-6">
          You are about to permanently delete all your data.
        </p>

        {/* Warning Box */}
        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                This action cannot be undone
              </h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{DATA_DELETION_MESSAGES.UI.IRREVERSIBLE_WARNING}</p>
                <p className="mt-1">{DATA_DELETION_MESSAGES.UI.DATA_DELETED_LIST}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <button
            onClick={handleConfirm}
            disabled={state === 'confirming'}
            className="w-full bg-red-600 text-white py-2 px-4 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {state === 'confirming' ? DATA_DELETION_MESSAGES.UI.LOADING : DATA_DELETION_MESSAGES.UI.CONFIRM_BUTTON}
          </button>
          <Link
            href="/"
            className="block w-full text-center text-gray-500 hover:text-gray-700 py-2"
          >
            Cancel and Return Home
          </Link>
        </div>
      </div>
    </div>
  );
}

